import {
  getTemplateDownloadUrl,
  getUploadUrl,
  parseUploadedExcel,
  ExcelUploadResult,
} from '../graphql';

export type { ExcelUploadResult };
export type { ExcelUrlGroup as UrlGroup } from '../graphql';

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadTemplate(): Promise<void> {
  const { url, filename } = await getTemplateDownloadUrl();
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download template: ${response.status} ${response.statusText}`.trim());
  }
  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}

export async function uploadExcelFile(file: File): Promise<ExcelUploadResult> {
  const { uploadUrl, fileKey } = await getUploadUrl(file.name);

  const ext = file.name.split('.').pop()?.toLowerCase();
  const contentType = ext === 'xlsx'
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'application/vnd.ms-excel';

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });

  if (!uploadResponse.ok) {
    const body = await uploadResponse.text().catch(() => '');
    const detail = body ? ` - ${body.trim()}` : '';
    throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}${detail}`.trim());
  }

  const result = await parseUploadedExcel(fileKey);
  if (!result.success) {
    throw new Error(result.message || 'Failed to parse Excel file');
  }
  return result;
}
